import React, {createContext, useEffect, useState} from 'react';
import Header from "@UIComponents/Header/Header";
import {BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate} from "react-router-dom";
import GamesList from "@gamePages/GamesList";
import InappsList from "@inappPages/InappsList";
import InappsPage from "@inappPages/InappsPage/InappsPage";
import Authorization from '@technicalPages/Authorization';
import Loading from "@UIComponents/Loading/Loading";
import Breadcrumbs from "@UIComponents/Breadcrumbs/Breadcrumbs";
import {TransitionGroup, CSSTransition} from "react-transition-group";
import {
    CALLBACK_PATH,
    ERROR_PATH,
    GAMES_PATH,
    HOME_PATH,
    INAPP_EDITING_PATH,
    INAPPS_GROUPS_PATH,
    INAPPS_LIST_ALL_PATH,
    INAPPS_LIST_SALE_PATH,
    LOGIN_PATH,
    NEW_INAPP_PATH,
    NEW_SALE_PATH,
    REFUNDS_PATH,
    SALE_INAPP_EDITING_PATH
} from "@constants/Paths";
import ErrorPage from "@technicalPages/ErrorPage";
import Notification from "@UIComponents/Notification/Notification";
import InappsCategories from "@inappPages/InappsCategories";
import SaleInapp from "@inappPages/InappsPage/SaleInappsPage";
import {useDispatch, useSelector} from "react-redux";
import getSales from "@actionCreators/salesActionCreator";
import getGames from "@actionCreators/gamesActionCreator";
import getTiers from "@actionCreators/tiersActionCreator";
import getInapps from "@actionCreators/inappsActionCreator";
import {ErrorBoundary} from "react-error-boundary";
import Fallback from "@helpers/Fallback";
import {GAME_QA_ROLE, MARKETING_QA_ROLE} from "@constants/ENV";
import {getAccessState, getDefaultRoute, hasGroup} from "@utils/helpers/access";
import RefundsPage from "@pages/Refunds/RefundsPage";

export const AppContext = createContext();

const hasAccessToken = () => Boolean(localStorage.getItem('accessToken'));

const LoginRoute = () => {
    const defaultRoute = getDefaultRoute();

    return hasAccessToken() && defaultRoute !== LOGIN_PATH
        ? <Navigate to={defaultRoute} replace/>
        : <Authorization/>;
};

const ProtectedRoute = ({isAllowed, fallback, children}) => {
    if (!hasAccessToken()) return <Navigate to={LOGIN_PATH} replace/>;
    if (!isAllowed) return fallback;

    return children;
};

const GamesRoute = ({children}) => {
    const access = getAccessState();

    return (
        <ProtectedRoute
            isAllowed={access.hasGamesAccess}
            fallback={<Navigate to={getDefaultRoute()} replace/>}
        >
            {children}
        </ProtectedRoute>
    );
};

const RefundRoute = () => {
    const access = getAccessState();

    return (
        <ProtectedRoute
            isAllowed={access.hasRefundAccess}
            fallback={<Fallback description="Sorry, you do not have sufficient permissions to view this page."/>}
        >
            <RefundsPage/>
        </ProtectedRoute>
    );
};

const GamesRoleRoute = ({blockedRole, children}) => (
    hasGroup(blockedRole)
        ? <Fallback description="Sorry, you do not have sufficient permissions to view this page."/>
        : children
);

const AnimatedSwitch = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [, setStorageVersion] = useState(0);

    useEffect(() => {
        const handleStorage = () => setStorageVersion(version => version + 1);
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    useEffect(() => {
        const isLoginRoute = location.pathname === LOGIN_PATH;
        const isCallbackRoute = location.pathname === CALLBACK_PATH;

        if (!hasAccessToken() && !isLoginRoute && !isCallbackRoute) {
            navigate(LOGIN_PATH, {replace: true});
            return;
        }

        if (isLoginRoute && hasAccessToken()) {
            const defaultRoute = getDefaultRoute();
            if (defaultRoute !== LOGIN_PATH) navigate(defaultRoute, {replace: true});
        }
    }, [location.pathname, navigate]);

    return (
        <TransitionGroup component={null}>
            <CSSTransition key={location.key} classNames="fade" timeout={600}>
                <Routes location={location}>
                    <Route path={LOGIN_PATH} element={<LoginRoute/>}/>
                    <Route
                        path={CALLBACK_PATH}
                        element={<Loading background='gray' text="Authorization in your account"/>}
                    />
                    <Route
                        path={GAMES_PATH}
                        element={<GamesRoute><GamesList/></GamesRoute>}
                    />
                    <Route
                        path={REFUNDS_PATH}
                        element={<RefundRoute/>}
                    />
                    <Route
                        path={INAPPS_GROUPS_PATH}
                        element={<GamesRoute><InappsCategories/></GamesRoute>}
                    />
                    <Route
                        path={INAPPS_LIST_ALL_PATH}
                        element={
                            <GamesRoute>
                                <GamesRoleRoute blockedRole={MARKETING_QA_ROLE}>
                                    <InappsList category={'all'}/>
                                </GamesRoleRoute>
                            </GamesRoute>
                        }
                    />
                    <Route
                        path={INAPPS_LIST_SALE_PATH}
                        element={
                            <GamesRoute>
                                <GamesRoleRoute blockedRole={GAME_QA_ROLE}>
                                    <InappsList category={'sales'}/>
                                </GamesRoleRoute>
                            </GamesRoute>
                        }
                    />
                    <Route
                        path={INAPP_EDITING_PATH}
                        element={
                            <GamesRoute>
                                <GamesRoleRoute blockedRole={MARKETING_QA_ROLE}>
                                    <InappsPage/>
                                </GamesRoleRoute>
                            </GamesRoute>
                        }
                    />
                    <Route
                        path={SALE_INAPP_EDITING_PATH}
                        element={
                            <GamesRoute>
                                <GamesRoleRoute blockedRole={GAME_QA_ROLE}>
                                    <SaleInapp/>
                                </GamesRoleRoute>
                            </GamesRoute>
                        }
                    />
                    <Route
                        path={NEW_INAPP_PATH}
                        element={
                            <GamesRoute>
                                <GamesRoleRoute blockedRole={MARKETING_QA_ROLE}>
                                    <InappsPage/>
                                </GamesRoleRoute>
                            </GamesRoute>
                        }
                    />
                    <Route
                        path={NEW_SALE_PATH}
                        element={
                            <GamesRoute>
                                <GamesRoleRoute blockedRole={GAME_QA_ROLE}>
                                    <SaleInapp/>
                                </GamesRoleRoute>
                            </GamesRoute>
                        }
                    />
                    <Route path={ERROR_PATH} element={<ErrorPage/>}/>
                    <Route path={HOME_PATH} element={<Navigate to={getDefaultRoute()}/>}/>
                    <Route path="*" element={<Navigate to={ERROR_PATH}/>}/>
                </Routes>
            </CSSTransition>
        </TransitionGroup>
    );
};

const AppContent = () => {
    const location = useLocation();
    const gameId = location.pathname.match(/games\/([^/]+)/)?.[1];
    const [inapps, setInapps] = useState([]);
    const [sales, setSales] = useState([]);

    const credentials = useSelector(state => state.userData);
    const dispatch = useDispatch();
    const games = useSelector(state => state.games);
    const tiers = useSelector(state => state.tiers);
    const inappsList = useSelector(state => state.inapps);
    const salesList = useSelector(state => state.sales);
    const isGamesRoute = location.pathname === GAMES_PATH || location.pathname.startsWith(`${GAMES_PATH}/`);
    const {hasGamesAccess} = getAccessState();

    useEffect(() => {
        if (!credentials || !isGamesRoute || !hasGamesAccess) return;

        getGames(dispatch, credentials);
        getTiers(dispatch, credentials);

        if (gameId && location.pathname !== `/games/${gameId}/sales-inapps`) {
            getSales(dispatch, credentials);
        }
        if (gameId && location.pathname !== `/games/${gameId}/all-inapps`) {
            getInapps(dispatch, credentials, gameId);
        }
    }, [credentials, dispatch, gameId, hasGamesAccess, isGamesRoute, location.pathname]);

    useEffect(() => {
        setInapps(inappsList);
    }, [inappsList]);

    useEffect(() => {
        setSales(salesList);
    }, [salesList]);

    return (
        <ErrorBoundary FallbackComponent={Fallback}>
            <AppContext.Provider value={{
                credentials,
                dispatch,
                games,
                tiers,
                inapps,
                setInapps,
                sales,
                setSales
            }}>
                <Header/>
                <Breadcrumbs/>
                <AnimatedSwitch/>
                <Notification/>
            </AppContext.Provider>
        </ErrorBoundary>
    );
};

function App() {
    return (
        <BrowserRouter>
            <AppContent/>
        </BrowserRouter>
    );
}

export default App;
